import { listLayers, getLayer, deleteLayer, shareLayer, unshareLayer } from '../api/layers.js';
import { auth } from '../auth/state.js';
import { toast } from './toast.js';
import { openAnalyticsModal } from './analyticsModal.js';
import { openStyleModal } from './styleModal.js';
import { openFeaturesEditor, registerUserLayerManager as registerEditorMgr } from './featuresEditor.js';
import { getStyle } from './styleStore.js';
import { listSkeleton } from './skeleton.js';
import { emptyState, ICON_LAYERS } from './emptyState.js';

const visibleLayers = new Set();

let userLayerManager = null;
let onChangeCallback = null;

export function initMyLayersPanel(manager, { onChange } = {}) {
  userLayerManager = manager;
  onChangeCallback = onChange;
  registerEditorMgr(manager);
  auth.onChange(() => render());
  render();
}

export function getVisibleLayerIds() {
  return [...visibleLayers];
}

export async function applyVisibleLayerIds(ids) {
  const wanted = new Set(ids);

  // Hide ones not in `ids`
  for (const id of [...visibleLayers]) {
    if (!wanted.has(id)) await toggle(id, false, /*silent*/ true);
  }

  // Fetch the user's full layer list once — avoids N round-trips below
  let allLayers = [];
  try { allLayers = await listLayers(); }
  catch (e) {
    console.warn('applyVisibleLayerIds: listLayers failed', e);
    return;
  }
  const byId = new Map(allLayers.map(l => [l.id, l]));

  // Show requested ones (skipping any that have since been deleted / lost access)
  for (const id of ids) {
    if (visibleLayers.has(id)) continue;
    const layer = byId.get(id);
    if (!layer) {
      console.info(`applyVisibleLayerIds: layer ${id} no longer accessible — skipping`);
      continue;
    }
    try {
      await userLayerManager.show(layer);
      visibleLayers.add(id);
    } catch (e) {
      console.warn(`applyVisibleLayerIds: failed to show layer ${id}`, e);
    }
  }
  await render();
}

async function render() {
  const host = document.getElementById('myLayersHost');
  if (!host) return;

  if (!auth.isAuthenticated()) {
    host.innerHTML = `
      <div class="lgroup-title">My layers</div>
      ${emptyState({
        icon:  ICON_LAYERS,
        title: 'Sign in to begin',
        body:  'Upload datasets, run analyses, share to the catalog. Free to create an account.',
      })}
    `;
    return;
  }

  // Show skeleton while the request is in flight
  host.innerHTML = `<div class="lgroup-title">My layers</div>${listSkeleton(3)}`;

  let layers = [];
  try {
    layers = await listLayers();
  } catch (e) {
    host.innerHTML = `
      <div class="lgroup-title">My layers</div>
      <div class="alert alert-error" style="margin:6px 8px">${escape(e.message)}</div>
    `;
    return;
  }

  if (layers.length === 0) {
    host.innerHTML = `
      <div class="lgroup-title">My layers</div>
      ${emptyState({
        icon:  ICON_LAYERS,
        title: 'Your first dataset',
        body:  'Open the Data tab and drop a Shapefile (.zip), GeoJSON, CSV, or GeoTIFF.',
      })}
    `;
    return;
  }

  const palette = ['#04648d', '#98c262', '#4ea3ce', '#e0a93a', '#d65a51', '#7a9bbb', '#b8d290', '#5b7d8c'];
  const itemHtml = layers.map(l => {
    const dot = palette[l.id % palette.length];
    let meta;
    if (l.status === 'ready') {
      if (l.layer_type === 'raster') {
        meta = `${l.band_count ?? '?'} band raster${l.source_format ? ' · ' + l.source_format : ''}`;
      } else {
        meta = `${l.feature_count ?? '–'} features${l.geometry_type ? ' · ' + l.geometry_type : ''}`;
      }
    } else if (l.status === 'processing') {
      meta = 'Processing…';
    } else {
      meta = `Failed: ${l.status_message || 'unknown'}`;
    }
    const isOn = visibleLayers.has(l.id);
    const disabled = l.status !== 'ready';
    const analyzable = l.status === 'ready' && l.layer_type === 'vector';
    const shareable  = l.status === 'ready';
    // Choropleth is a polygon-only cartographic technique — points and lines
    // would be graduated symbology, which is a different feature.
    // Any ready vector layer can be styled — single style for all geometry
    // types, plus graduated when a numeric attribute exists.
    const styleable  = l.status === 'ready' && l.layer_type === 'vector';
    const editable   = l.status === 'ready' && l.layer_type === 'vector';
    const styled     = !!getStyle(l.id)?.applied;
    const shareTitle = l.is_public ? 'Shared in catalog · click to make private' : 'Share to catalog';
    return `
      <div class="litem ${isOn ? 'on' : ''}" data-user-layer="${l.id}" style="${disabled ? 'opacity:.55;pointer-events:none' : ''}">
        <div class="ltoggle"></div>
        <div class="ldot" style="background:${dot}"></div>
        <div style="flex:1;min-width:0">
          <div class="lname" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
            ${escape(l.name)}
            ${l.is_public ? '<span class="ml-share-badge" title="Shared in catalog">●</span>' : ''}
          </div>
          <div class="lmeta">${escape(meta)}</div>
        </div>
        ${shareable ? `
          <button class="rpanel-close" data-share-layer="${l.id}" data-is-public="${l.is_public ? '1' : '0'}" title="${shareTitle}" style="pointer-events:auto">
            ${l.is_public
              ? `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>`
              : `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>`}
          </button>
        ` : ''}
        ${styleable ? `
          <button class="rpanel-close" data-style-layer="${l.id}" title="${styled ? 'Style applied · click to edit' : 'Style this layer'}" style="pointer-events:auto">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="${styled ? 'var(--primary)' : 'currentColor'}" stroke-width="2">
              <path d="M19 11h2v2h-2zM17 13h2v2h-2zM15 15h2v2h-2zM3 11h12v2H3z"/>
              <path d="M3 5h18M3 17h12"/>
            </svg>
          </button>
        ` : ''}
        ${editable ? `
          <button class="rpanel-close" data-edit-layer="${l.id}" title="Edit attributes" style="pointer-events:auto">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
          </button>
        ` : ''}
        ${analyzable ? `
          <button class="rpanel-close" data-analyze-layer="${l.id}" title="Run analysis" style="pointer-events:auto">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 3v18h18"/><path d="M7 14l3-3 3 3 5-5"/></svg>
          </button>
        ` : ''}
        <button class="rpanel-close" data-delete-layer="${l.id}" title="Delete layer" style="pointer-events:auto">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M6 6l1 14a2 2 0 002 2h6a2 2 0 002-2l1-14"/></svg>
        </button>
      </div>
    `;
  }).join('');

  host.innerHTML = `
    <div class="lgroup-title" style="display:flex;justify-content:space-between;align-items:center">
      <span>My layers</span>
      <button class="btn-sm btn-outline" data-action="refreshMyLayers" style="padding:2px 8px;font-size:10px">Refresh</button>
    </div>
    ${itemHtml}
  `;
}

async function toggle(layerId, forceState, silent = false) {
  if (!userLayerManager) return;
  const turningOn = forceState ?? !visibleLayers.has(layerId);
  try {
    if (turningOn) {
      const layer = (await listLayers()).find(l => l.id === layerId);
      if (!layer) throw new Error('Layer not found');
      await userLayerManager.show(layer);
      visibleLayers.add(layerId);
    } else {
      userLayerManager.hide(layerId);
      visibleLayers.delete(layerId);
    }
    if (!silent) await render();
    onChangeCallback?.();
  } catch (e) {
    toast(`Could not toggle layer: ${e.message}`);
  }
}

export async function handleMyLayerClick(target) {
  // Style button (paint bucket)
  const styleBtn = target.closest('[data-style-layer]');
  if (styleBtn) {
    const id = +styleBtn.dataset.styleLayer;
    try {
      const layer = await getLayer(id);
      // Auto-show on map first if it isn't visible yet
      if (!userLayerManager.isLoaded(id)) {
        await userLayerManager.show(layer);
        visibleLayers.add(id);
        await render();
      }
      openStyleModal(layer, { userLayerManager });
    } catch (e) {
      toast(`Could not open style: ${e.message}`);
    }
    return true;
  }

  // Edit button (attribute table)
  const editBtn = target.closest('[data-edit-layer]');
  if (editBtn) {
    const id = +editBtn.dataset.editLayer;
    await openFeaturesEditor(id);
    return true;
  }

  // Analyze button
  const analyzeBtn = target.closest('[data-analyze-layer]');
  if (analyzeBtn) {
    const id = +analyzeBtn.dataset.analyzeLayer;
    try {
      const layer = await getLayer(id);
      openAnalyticsModal(layer, { onLayerCreated: () => render() });
    } catch (e) {
      toast(`Could not open analysis: ${e.message}`);
    }
    return true;
  }

  // Share toggle
  const shareBtn = target.closest('[data-share-layer]');
  if (shareBtn) {
    const id = +shareBtn.dataset.shareLayer;
    const isPublic = shareBtn.dataset.isPublic === '1';
    try {
      if (isPublic) {
        await unshareLayer(id);
        toast('Layer is now private');
      } else {
        await shareLayer(id);
        toast('Layer shared to catalog');
      }
      await render();
      onChangeCallback?.();
    } catch (e) {
      toast(`Could not toggle share: ${e.message}`);
    }
    return true;
  }

  // Delete button next
  const deleteBtn = target.closest('[data-delete-layer]');
  if (deleteBtn) {
    const id = +deleteBtn.dataset.deleteLayer;
    if (!confirm('Delete this layer? This cannot be undone.')) return;
    try {
      userLayerManager?.remove(id);
      visibleLayers.delete(id);
      await deleteLayer(id);
      toast('Layer deleted');
      await render();
      onChangeCallback?.();
    } catch (e) {
      toast(`Delete failed: ${e.message}`);
    }
    return true;
  }

  const item = target.closest('[data-user-layer]');
  if (item) {
    await toggle(+item.dataset.userLayer);
    return true;
  }

  return false;
}

export const refreshMyLayers = render;

/** Programmatically toggle a layer ON (used after upload to auto-show). */
export async function showLayerOnMap(layerId) {
  await toggle(layerId, true);
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}
